import { extend, isEmpty, result, noop } from 'underscore';
import Promise from 'bluebird';
import { assign } from 'xstate';
import { readCampaignNegativeWebpages, readDayTimeCriterions, readLocationCriterions, readNewCustomerAcquisitionGoals } from '@bingads-webui-campaign-react/perf-max-data';
import { showBudgetBidCampaignNotifications } from '@bingads-webui-campaign/import-shared-utils';
import instrumentation from '@bingads-webui-campaign-legacy/instrumentation/index';
import { initialBrandExclusionsViewModel, initialCampaignViewModel, loadCampaign, modelToViewModelForCampaign, viewModelToModelForCampaign } from '../../model/campaign';
import { getErrorsPlaceholders, saveCampaignAndLinkedData } from '../../model/campaign-bulk';
import { loadBundleCountries } from '../../model/campaign-criterions';
import { loadConversionGoalDetails } from '../../model/conversion-goals-details';

const getCampaignId = context =>
  result(context, ['data', 'campaign', 'data', 'Id']) ||
  result(context, ['stepsData', 'campaignId']);

/**
 * Loads step data asynchronously. The returned promise must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 *
 * The function is called when the wizard enters the step.
 * The step will be blocked until the promise resolves.
 * @param {Object} context - the current context
 * @returns {Promise<Object>} a promise that must resolve
 * into a single object, opaque to the step, which will be saved
 * in the context under the `data` property.
 */
export const loadData = (context) => {
  const {
    cid, aid, serverContext,
  } = context.stepsData;
  const { activity = { trace: noop } } = context.data;
  activity.trace(`[PmaxWizard] loadData started in campaign settings step. StepId: ${context.id}`);
  const campaignId = getCampaignId(context);
  const { Permissions, DynamicPermissions } = serverContext || {};
  const permissions = { dynamic: { ...DynamicPermissions }, ...Permissions };
  const {
    NewCustomerAcquisition: isNewCustomerAcquisitionEnabled,
  } = permissions.dynamic;

  let loadPromises = {
    campaign: Promise.resolve(initialCampaignViewModel),
    brandExclusions: Promise.resolve(initialBrandExclusionsViewModel),
    bundleCountriesAndLocationCriterions: loadBundleCountries({
      cid,
      aid,
      serverContext,
    }),
    conversionGoalDetails: loadConversionGoalDetails({ cid, aid }),
    ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoals: readNewCustomerAcquisitionGoals(cid, aid) }),
  };
  if (campaignId) {
    loadPromises = extend(loadPromises, {
      campaign: loadCampaign(cid, aid, campaignId),
      // Note: CampaignCriterions are not loaded, but only saved during step life-time.
      // It is the responsibility of various targeting controls to load the criterions they manage.
      // As an optimization, there are two options:
      // 1) Use preFetch (shown below) to pre-load parallel with main data;
      // 2) In addition to #1, use stepsData for storing and retrieving
      //    last edited criterions (can have stale data).
      locationCriterions: readLocationCriterions(cid, aid, campaignId),
      dayTimeCriterions: readDayTimeCriterions(cid, aid, campaignId),
      campaignNegativeWebpages: readCampaignNegativeWebpages(cid, aid, campaignId),
    });
  }
  return Promise.props(loadPromises).then(({
    bundleCountriesAndLocationCriterions,
    campaign,
    brandExclusions,
    locationCriterions,
    dayTimeCriterions,
    campaignNegativeWebpages,
    conversionGoalDetails,
    newCustomerAcquisitionGoals,
  }) => {
    let results = {
      ...bundleCountriesAndLocationCriterions,
      campaign,
      brandExclusions,
      conversionGoalDetails,
      ...getErrorsPlaceholders(),
    };
    if (campaignId) {
      const campaignData = modelToViewModelForCampaign({
        campaignModel: campaign,
        locationCriterionsModel: locationCriterions ? locationCriterions.value : [],
        dayTimeCriterionsModel: dayTimeCriterions ? dayTimeCriterions.value : [],
        campaignNegativeWebpagesModel: campaignNegativeWebpages ? campaignNegativeWebpages.value : [],
        bundleCountries: bundleCountriesAndLocationCriterions.bundleCountries,
        ...(isNewCustomerAcquisitionEnabled && { newCustomerAcquisitionGoalsModel: newCustomerAcquisitionGoals ? newCustomerAcquisitionGoals.value : [] }),
      });
      results = extend(results, {
        campaign: campaignData,
        originalCampaign: { ...campaignData },
      });
    }
    activity.trace(`[PmaxWizard] loadData succeeded in campaign settings step. StepId: ${context.id}`);
    return results;
  }).catch((errorData) => {
    activity.trace(`[PmaxWizard] loadData failed. Error:  ${JSON.stringify(errorData)}`);

    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      error: {
        ...errorData,
      },
    });
  });
};

export const saveData = (context) => {
  const { activity = { trace: noop } } = context.data;

  return saveCampaignAndLinkedData({
    campaign: context.data.campaign,
    originalCampaign: context.data.originalCampaign,
    brandExclusions: context.data.brandExclusions,
    campaignCriterions: context.data.campaignCriterions,
    bundleLocationCriterions: context.data.bundleLocationCriterions,
    bundleCountries: context.data.bundleCountries,
  }, {
    scenario: context.stepsData.scenarioName,
    activityLogger: context.stepsData.scenario.createActivity('saveCampaignAndLinkedData'),
    i18n: context.stepsData.i18n,
  }).then((data) => {
    activity.trace(`[PmaxWizard] saveData succeeded. StepId: ${context.id}`);
    const { activity: BudgetBidCampaignImportNotificationActivity } = instrumentation.createScenarioAndActivity({
      scenarioName: 'BudgetBidCampaignImportNotification',
      activityName: 'PmaxCampaignSettingPage',
    });

    const initialCampaign = context.data.originalCampaign.data;
    const newCampaign = context.data.campaign.data;
    showBudgetBidCampaignNotifications({
      campaignItemList: [initialCampaign],
      oldBudget: initialCampaign.Budget,
      newBudget: newCampaign.Budget,
      oldBidStrategy: initialCampaign.BiddingScheme,
      newBidStrategy: newCampaign.BiddingScheme,
      activity: BudgetBidCampaignImportNotificationActivity,
    });
    return data;
  }).catch((bulkOrSystemError) => {
    activity.trace(`[PmaxWizard] saveData failed. StepId: ${context.id}`);
    return Promise.reject(bulkOrSystemError);
  });
};


export const needsLoading = () => true;

export const isValid = context => context.data.campaign.isValid;
export const isDirty = context => context.data.campaign.isDirty || context.data.brandExclusions.isDirty;
const hasServerErrors = context => !isEmpty(context.data.serverErrors) || !isEmpty(context.data.systemErrors);
export const needsSaving = context => isDirty(context) || hasServerErrors(context);

export const patchStepsData = assign({
  stepsData: (context/* , event */) => ({
    ...context.stepsData,
    campaign: viewModelToModelForCampaign(context.data.campaign),
  }),
});
export const clearErrors = assign({
  data: context => ({
    ...context.data,
    ...getErrorsPlaceholders(),
  }),
});
export const hasIncomingSystemError = () => false;
